import { KeyValue } from "../../interfaces";
import { generateFinancialsApiUrl } from "../FinancialReporting/FinancialReportingUtilities";

export const standardComparisonOptions = [
  { value: "", label: "Select a Standard Peer Grouping" },
  {
    value: "allCommercialSavings",
    label: "All vs. Commercial vs. Savings",
    groups: ["allInst", "allCommercial", "allSavings"],
  },
  { value: "allInst", label: "All Institutions", groups: ["allInst"] },
  {
    value: "allCommercial",
    label: "Commercial Institutions",
    groups: ["allCommercial"],
  },
  {
    value: "allSavings",
    label: "Savings Institutions",
    groups: ["allSavings"],
  },
  {
    value: "fedCharters",
    label: "Federal Charters",
    groups: ["fedCharters"],
  },
  {
    value: "stateCharters",
    label: "State Charters",
    groups: ["stateCharters"],
  },
  {
    value: "assetSize",
    label: "All Institutions - Assets Size Distribution",
    groups: [
      "allInst",
      "lessThan100M",
      "100Mto1B",
      "1Bto10B",
      "10Bto250B",
      "greaterThan250B",
    ],
  },
  {
    value: "geographicRegion",
    label: "All Institutions - By Geographic Region",
    groups: [
      "allInst",
      "instNY",
      "instATL",
      "instCHI",
      "instKC",
      "instDAL",
      "instSF",
    ],
  },
  {
    value: "assetConcentration",
    label: "All Institutions - By Asset Concentration",
    groups: [
      "allInst",
      "CCBanks",
      "international",
      "agriculture",
      "commercial",
      "mortgage",
      "consumer",
      "specializedLessThan1B",
      "othLessThan1B",
      "othGreaterThan1B",
    ],
  },
  {
    value: "federalStateCharters",
    label: "All Institutions - Federal vs. State Charters",
    groups: ["allInst", "fedCharters", "stateCharters"],
  },
];

export const subOptionLookup: KeyValue = {
  "allInst": "All Institutions",
  "lessThan100M": "Less than $100M",
  "100Mto1B": "$100M - $1B",
  "1Bto10B": "$1B - $10B",
  "10Bto250B": "$10B - $250B",
  "greaterThan250B": "Greater than $250B",
  "CCBanks": "Credit Card Banks",
  "international": "International Banks",
  "agriculture": "Agricultural Banks",
  "allCommercial": "Commercial Institutions",
  "allSavings": "Savings Institutions",
  "commercial": "Commercial Lenders",
  "mortgage": "Mortgage Lenders",
  "consumer": "Consumer Lenders",
  "fedCharters": "Federal Charters",
  "stateCharters": "State Charters",
  "specializedLessThan1B": "All Other Specialized < $1B",
  "othLessThan1B": "All Other < $1B",
  "othGreaterThan1B": "All Other > $1B",
  "instNY": "New York",
  "instATL": "Atlanta",
  "instCHI": "Chicago",
  "instKC": "Kansas City",
  "instDAL": "Dallas",
  "instSF": "San Francisco",
};

export const sumFieldsLookup: KeyValue = {
  NTINCL: ["NTINCL", "INSTCNT"],
  NTINCLQ: ["NTINCLQ", "INSTCNT"],
  NTINCHPP: ["NTINCHPP", "INSTCNT"],
  NTINQHPP: ["NTINQHPP", "INSTCNT"],
  INTINCY: ["INTINCA", "ERNAST5"],
  INTINCYQ: ["INTINQA", "ERNAST2"],
  INTEXPY: ["EINTEXPA", "ERNAST5"],
  INTEXPYQ: ["EINTXQA", "ERNAST2"],
  NIMY: ["NIMA", "ERNAST5"],
  NONIIAY: ["NONIIA", "ASSET5"],
  NONIIAYQ: ["NONIIQA", "ASSET2"],
  NIMYQ: ["NIMQA", "ERNAST2"],
  NONIXAY: ["NONIXA", "ASSET5"],
  NONIXAYQ: ["NONIXQA", "ASSET2"],
  ELNATRY: ["ELNATRA", "ASSET5"],
  ELNATRYQ: ["ELNATQA", "ASSET2"],
  NOIJY: ["NOIJA", "ASSET5"],
  NOIJYQ: ["NOIJQA", "ASSET2"],
  ROA: ["NETINCA", "ASSET5"],
  ROAQ: ["NETINCQA", "ASSET2"],
  ROAPTX: ["NETINBXA", "ASSET5"],
  ROAPTXQ: ["NETIBXQA", "ASSET2"],
  ROE: ["NETINCA", "EQ5"],
  ROEQ: ["NETINCQA", "EQ2"],
  ROEINJR: ["NTIRTA", "EQ5"],
  NTLNLSR: ["NTLNLSA", "LNLSGR5"],
  NTLNLSRQ: ["NTLNLSQA", "LNLSGR2"],
  NTNER: ["NTREA", "LNRE5"],
  NTNERQ: ["NTREQA", "LNRE2"],
  NTRECOSR: ["NTRECOSA", "LNRECON5"],
  NTRECOQR: ["NTRECOQA", "LNRECON2"],
  NTRENRSR: ["NTRENRSA", "LNRENRE5"],
  NTRENRQR: ["NTRENRSQ", "LNRENRE2"],
  NTREMULR: ["NTREMULA", "LNREMUL5"],
  NTREMUQR: ["NTREMUQA", "LNREMUL2"],
  NTRERESR: ["NTRERESA", "LNRERES5"],
  NTRERESRQ: ["NTRERESQ", "LNRERES2"],
  NTRELOCR: ["NTRELOCA", "LNRELOC5"],
  NTRELOCRQ: ["NTRELOCA", "LNRELOC5"],
  NTREOTHR: ["NTREOTHA", "LNREOTH5"],
  NTREOTHRQR: ["NTREOTQA", "LNREOTH2"],
  NTCONOTR: ["NTCONOTA", "LNCONOT5"],
  IDNTCIR: ["NTCIA", "LNCI5"],
  IDNTCIQR: ["NTCIQ", "LNCI5"],
  IDNTCONR: ["NTCONA", "LNCON5"],
  IDNTCRDR: ["NTCRCDA", "LNCRCD5"],
  IDNTCRDQR: ["NTCRCDQ", "LNCRCD2"],
  NTAUTOPR: ["NTAUTOA", "LNAUTO5"],
  NTAUTOQR: ["NTAUTOQ", "LNAUTO2"],
  IDNTCOTR: ["NTCONOTA", "LNCONOT5"],
  NTCOMRER: ["NTCOMREA", "LNCOMRE5"],
  NTCOMREQR: ["NTCOMREQ", "LNCOMRE2"],
  ELNANTR: ["ELNLOS", "NTTOT"],
  ELNATQR: ["ELNLOSQ", "NTTOTQ"],
  EEFFQR: ["EEFFQ", "IEFFQ"],
  EQCDIVNTINC: ["EQCDIVA", "NETINCA"],
  IDERNCVR: ["CHFLA", "NTLNLSA"],
  IDERNCVQ: ["CHFLQ", "NTLNLSQ"],
  LNATRESR: ["LNATRES", "LNLSGR"],
  LNRESNCR: ["LNATRESJ", "NCLNLS"],
  NPERFV: ["NPERF", "ASSET"],
  NCLNLSR: ["NCLNLS", "LNLSGRJ"],
  IDNCGTPR: ["NCGTYPAR", "NCLNLS"],
  LNLSNTV: ["LNLSNET", "ASSET"],
  LNLSDEPR: ["LNLSNET", "DEP"],
  IDLNCORR: ["LNLSNET", "COREDEP"],
  DEPDASTR: ["DEPDOM", "ASSET"],
  EQV: ["EQ", "ASSET"],
  RBC1AAJ: ["RBCT1", "AVASSETJ"],
  RBCRWAJ: ["RBC", "RWAJT"],
  ESTINS: ["DEPINS", "DEPBEFEX", "DEPALLEX"],
  NTRER: ["NTREA", "LNRE5"],
  NCRER: ["NCRE", "LNREJ"],
  NCRECONR: ["NCRECONS", "LNRECONS"],
  NCRENRER: ["NCRENRES", "LNRENRES"],
  NCRERESR: ["NCRERES", "LNRERES"],
  NCRELOCR: ["NCRELOC", "LNRELOC"],
  IDNCCIR: ["NCCI", "LNCI"],
  IDNCCONR: ["NCCON", "LNCON"],
  IDNCCRDR: ["NCCRCD", "LNCRCD"],
  IDNCATOR: ["NCAUTO", "LNAUTO"],
  IDNCCOTR: ["NCCONOTH", "LNCONORP"],
  NCCOMRER: ["NCCOMRE", "LNCOMRE"],
  IDNCCOOR: ["NCCONOTH", "NCAUTO", "LNCONORP", "LNAUTO"],
  IDNTCOOR: ["NTCONOTA", "NTAUTOA", "LNCONOT5", "LNAUTO5"],
  IDNTCOOQR: ["NTCONOTQ", "NTAUTOQ", "LNCONOT2", "LNAUTO2"],
  NTALLOTHR: [
    "NTDEP",
    "NTFORGV",
    "NTOTHER",
    "NTLS",
    "NTAGA",
    "LNOTCI5",
    "LNAG5",
  ],
  NTALLOTHQR: [
    "NTDEPQ",
    "NTFORGVQ",
    "NTOTHQ",
    "NTLSQ",
    "NTAGQ",
    "LNOTCI2",
    "LNAG22",
  ],
  IDNCOTHR: ["NCDEP", "NCFG", "NCOTHLN", "NCLS", "NCAG", "LNOTCI", "LNAG"],
  ASTEMPM: ["ASSET", "NUMEMP"],
  LNATRESRR: ["LNATRES", "ASSET"],
  P3RENRESR: ["P3RENREN", "ASSET"],
  UCOTHERR: ["UCOTHE", "ASSET"],
  AOAR: ["OA", "INVSUB", "INVSUORE", "OA", "INVSUB", "CUSLI", "ASSET"],
  P3RELNDOR: ["P3RECONS", "P3REAG", "P3RERES", "P3REMULT", "P3RENRES", "ASSET"],
  P9RELNDOR: ["P9RECONS", "P9REAG", "P9RERES", "P9REMULT", "P9RENRES", "ASSET"],
  NARELNDOR: ["NARECONS", "NAREAG", "NARERES", "NAREMULT", "NARENRES", "ASSET"],
  LNLSGRSR: ["LNLSGR", "LNCONTRA", "UNINC", "LNLS", "ASSET"],
  SCRMBPIR: ["SCODPC", "SCODPI", "ASSET"],
  SCCMOSR: ["SCCMMB", "SCCPTG", "SCCMOG", "ASSET"],
  SCCMPTR: ["SCCMPT", "ASSET"],
  TRREVALSUMR: ["TRREVALD", "TRREVALF", "ASSET"],
  LNDEPAOBKR: ["LNDEPAC", "LNDEPACD", "ASSET"],
  LNCIFORR: ["LNCIFOR", "ASSET"],
  LNCINUSFR: ["LNCINUSF", "ASSET"],
  NTLNLSCOR: ["NTLNLS", "ASSET5"],
  NTRERESLNR: ["NTRERES", "ASSET5"],
  NTRELOCLNR: ["NTRELOC", "ASSET5"],
  NCREMULR: ["NCREMULT", "LNREMULT"],
  IDERNCVQR: ["CHFLQ", "NTLNLSQ"],
  NTLNLSQR: ["NTLNLSQA", "LNLSGR2"],
  NTREQR: ["NTREQA", "LNRE2"],
  NTRERESQR: ["NTRERESQ", "ASSET2"],
  NTCONTQR: ["NTCONOTQ", "LNCONOT2"],
  EEFFR: ["EEFF", "IEFF"],
  ASSETR: ["ASSET", "ASSET"],
  NTRERQ: ["NTREQ", "ASSET2"],
  NTRELNR: ["NTRE", "ASSET5"],
  NCREREOR: ["NCRERESO", "LNREOTH"],
};

const OTHER_RATIO_CONDITIONS = [
  "ASSETR",
  "LNATRESRR",
  "ESTINS",
  "P3RENRESR",
  "UCOTHERR",
  "AOAR",
  "P3RELNDOR",
  "P9RELNDOR",
  "NARELNDOR",
  "LNLSGRSR",
  "SCRMBPIR",
  "SCCMOSR",
  "SCCMPTR",
  "TRREVALSUMR",
  "LNDEPAOBKR",
  "LNCIFORR",
  "LNCINUSFR",
  "NTLNLSCOR",
  "NTRERESLNR",
  "NTRELOCLNR",
  "NTRERQ",
];

const INCOME_EXPENSE_YTD = [
  "INTINCR",
  "ILNDOMR",
  "ILNFORR",
  "ILSR",
  "ICHBALR",
  "ISCR",
  "ITRADER",
  "IFREPOR",
  "IOTHIIR",
  "EINTEXPR",
  "EDEPDOMR",
  "EDEPFORR",
  "EFREPPR",
  "EOTHINTR",
  "NIMR",
  "ELNATRR",
  "NONIIR",
  "IFIDUCR",
  "ISERCHGR",
  "IGLTRADR",
  "IGLRTEXR",
  "IGLFXEXR",
  "IGLEDEXR",
  "IGLCMEXR",
  "IGLCREXR",
  "ADDNONIIR",
  "IINVFEER",
  "IVENCAPR",
  "ISERFEER",
  "ISECZR",
  "IINSCOMR",
  "IINSUNDR",
  "IINSOTHR",
  "NETGNSLNR",
  "NETGNSRER",
  "NETGNASTR",
  "IOTNIIR",
  "NONIXR",
  "ESALR",
  "EPREMAGGR",
  "ADDNONINTEXPR",
  "EAMINTANR",
  "EINTOTHR",
  "EINTGWR",
  "EOTHNINTR",
  "PTAXNETINCR",
  "IGLSECR",
  "ITAXR",
  "IBEFXTRR",
  "EXTRAR",
  "NETINBMR",
  "NETIMINR",
  "NETINCR",
  "NTRELNR",
  "NTRENUSR",
  "NTREOFFDOMR",
  "NTRECONSR",
  "NTREAGR",
  "NTRERSFMR",
  "NTRERSF2R",
  "NTREMULTR",
  "NTRENRESR",
  "NTREFORR",
  "NTDEPR",
  "NTDEPNUSR",
  "NTAGR",
  "NTCIR",
  "NTCINUSR",
  "NTCONR",
  "NTCRCDR",
  "NTAUTOR",
  "NTCONOTHR",
  "NTFORGVR",
  "NTOTHERR",
  "NTLSR",
  "NTAGSMR",
  "DRLNLSR",
  "DRRER",
  "DRRENUSR",
  "DRREOFFDOMR",
  "DRRECONSR",
  "DRREAGR",
  "DRRERESR",
  "DRRERSFMR",
  "DRRERSF2R",
  "DRRELOCR",
  "DRREMULTR",
  "DRRENRESR",
  "DRREFORR",
  "DRDEPR",
  "DRDEPNUSR",
  "DRAGR",
  "DRCIR",
  "DRCINUSR",
  "DRCONR",
  "DRCRCDR",
  "DRAUTOR",
  "DRCONOTHR",
  "DRFORGVR",
  "DROTHERR",
  "DRLSR",
  "DRAGSMR",
  "CRLNLSR",
  "CRRER",
  "CRRENUSR",
  "CRREOFFDOMR",
  "CRRECONSR",
  "CRREAGR",
  "CRRERESR",
  "CRRERSFMR",
  "CRRERSF2R",
  "CRRELOCR",
  "CRREMULTR",
  "CRRENRESR",
  "CRREFORR",
  "CRDEPR",
  "CRDEPNUSR",
  "CRAGR",
  "CRCIR",
  "CRCINUSR",
  "CRCONR",
  "CRCRCDR",
  "CRAUTOR",
  "CRCONOTHR",
  "CRFORGVR",
  "CROTHERR",
  "CRLSR",
  "CRAGSMR",
  "EQCDIVR",
  "EQCDIVPR",
  "EQCDIVCR",
  "EQCSTKRXR",
  "NOIJR",
];

const INCOME_EXPENSE_QTLY = [
  "INTINQR",
  "ILNDOMQR",
  "ILNFORQR",
  "ILSQR",
  "ICHBALQR",
  "ISCQR",
  "ITRADEQR",
  "IFREPOQR",
  "IOTHIIQR",
  "EINTXQR",
  "EDEPDOMQR",
  "EDEPFORQR",
  "EFREPPQR",
  "EOTHINTQR",
  "NIMQR",
  "NfONIIQR",
  "IFIDUCQR",
  "ISERCHGQR",
  "IGLTRDQR",
  "IGLRTEXQR",
  "IGLFXEXQR",
  "IGLEDEXQR",
  "IGLCMEXQR",
  "IGLCREXQR",
  "ADDNONIIQR",
  "IINVFEEQR",
  "IVENCAPQR",
  "ISERFEEQR",
  "ISECZQR",
  "IINSCOMQR",
  "IINSUNDQR",
  "IINSOTHQR",
  "NTGLLNQR",
  "NTGLREQR",
  "NTGLFXAQR",
  "IOTNIIQR",
  "NONIXQR",
  "ESALQR",
  "EPREMAGQR",
  "ADDNONINTEXPQR",
  "EAMINTQR",
  "EINTOTHQR",
  "EINTGWQR",
  "EOTHNINQR",
  "PTAXNETINCQR",
  "IGLSECQR",
  "ITAXQR",
  "IBEFXTRQR",
  "EXTRAQR",
  "NETINBMQR",
  "NETIMINQR",
  "NETINCQR",
  "NTLNLSCOQR",
  "NTRENUSQR",
  "NTREOFFDOMQR",
  "NTRECONQR",
  "NTREAGQR",
  "NTRERESQR",
  "NTRERSFQR",
  "NTRERS2QR",
  "NTRELOCQR",
  "NTREMULQR",
  "NTRENRSQR",
  "NTREFORQR",
  "NTDEPQR",
  "NTDEPNUQR",
  "NTAGQR",
  "NTCIQR",
  "NTCINUSQR",
  "NTCONQR",
  "NTCRCDQR",
  "NTAUTOLNQR",
  "NTCONOTQR",
  "NTFORGVQR",
  "NTOTHQR",
  "NTLSQR",
  "NTAGSMQR",
  "DRLNLSQR",
  "DRREQR",
  "DRRENUSQR",
  "DRREOFFDOMQR",
  "DRRECONQR",
  "DRREAGQR",
  "DRRERESQR",
  "DRRERSFQR",
  "DRRERS2QR",
  "DRRELOCQR",
  "DRREMULQR",
  "DRRENRSQR",
  "DRREFORQR",
  "DRDEPQR",
  "DRDEPNUQR",
  "DRAGQR",
  "DRCIQR",
  "DRCINUSQR",
  "DRCONQR",
  "DRCRCDQR",
  "DRAUTOQR",
  "DRCONOTQR",
  "DRFORGVQR",
  "DROTHQR",
  "DRLSQR",
  "DRAGSMQR",
  "CRLNLSQR",
  "CRREQR",
  "CRRENUSQR",
  "CRREOFFDOMQR",
  "CRRECONQR",
  "CRREAGQR",
  "CRRERESQR",
  "CRRERSFQR",
  "CRRERS2QR",
  "CRRELOCQR",
  "CRREMULQR",
  "CRRENRSQR",
  "CRREFORQR",
  "CRDEPQR",
  "CRDEPNUQR",
  "CRAGQR",
  "CRCIQR",
  "CRCINUSQR",
  "CRCONQR",
  "CRCRCDQR",
  "CRAUTOQR",
  "CRCONOTQR",
  "CRFORGVQR",
  "CROTHQR",
  "CRLSQR",
  "CRAGSMQR",
  "EQCDIVQR",
  "EQCSXQR",
  "NOIJQR",
  "NONIIQR",
];

const ASSET_LIABILITIES_RATIO = [
  "CHBALR",
  "CHBALNIR",
  "CHBALIR",
  "CHCICR",
  "CHITEMR",
  "CHCOINR",
  "CHUSR",
  "CHNUSR",
  "CHFRBR",
  "SCR",
  "SCUSR",
  "SCUSTR",
  "SCUSOR",
  "SCASPNSUMR",
  "SCFMNR",
  "SCGNMR",
  "SCCOLR",
  "SCCPTGR",
  "SCCMOGR",
  "SCMUNIR",
  "SCDOMOR",
  "SCABSR",
  "SCSFPR",
  "SCODOTR",
  "SCFORDR",
  "SCEQFVR",
  "SCHTMRESR",
  "SCPLEDGER",
  "SCMTGBKR",
  "SCGTYR",
  "SCODPCR",
  "SCODPIR",
  "SCCMOTR",
  "SCHAR",
  "SCAFR",
  "SCRDEBTR",
  "SCPT3LESR",
  "SCPT3T12R",
  "SCP1T3R",
  "SCPT3T5R",
  "SCPT5T15R",
  "SCPTOV15R",
  "SCO3YLESR",
  "SCOOV3YR",
  "SCNM3LESR",
  "SCNM3T12R",
  "SCNM1T3R",
  "SCNM3T5R",
  "SCNM5T15R",
  "SCNMOV15R",
  "SC1LESR",
  "SCSNHAAR",
  "SCSNHAFR",
  "TRADER",
  "TRLREVALR",
  "FREPOR",
  "LNLSNETR",
  "LNLSGRR",
  "LNCONTRAR",
  "LNRER",
  "LNREDOMR",
  "LNRECONSR",
  "LNRENRESR",
  "LNREMULTR",
  "LNRERESR",
  "LNREAGR",
  "LNREFORR",
  "LNAGR",
  "LNCIR",
  "LNCONR",
  "LNCRCDR",
  "LNCONRPR",
  "LNAUTOR",
  "LNCONOTHR",
  "LNOTCIR",
  "LNFGR",
  "LNMUNIR",
  "LNSOTHERR",
  "LSR",
  "LNCOMRER",
  "LNRENUSR",
  "LNPLEDGER",
  "RB2LNRESR",
  "LNLSSALER",
  "LNEXAMTR",
  "LNRENROWR",
  "LNRENROTR",
  "LNRERSFMR",
  "LNRERSF2R",
  "LNRELOCR",
  "LNRERSF1R",
  "LNRECNFMR",
  "LNRECNOTR",
  "RSLNTOTR",
  "RSLNREFMR",
  "RSLNLSR",
  "P3RSLNLTR",
  "P3RSLNFMR",
  "P3RSLNLSR",
  "P9RSLNLTR",
  "P9RSLNFMR",
  "P9RSLNLSR",
  "NARSLNLTR",
  "NARSLNFMR",
  "NARSLNLSR",
  "LNLSGRFR",
  "UNINCFORR",
  "LNLSFORR",
  "LNDEPCBFR",
  "LNDEPUSFR",
  "LNDEPFCFR",
  "LNAGFORR",
  "LNCONFORR",
  "LNFGFORR",
  "LNMUNIFR",
  "LNOTHERFR",
  "LSFORR",
  "LNRS3LESR",
  "LNRS3T12R",
  "LNRS1T3R",
  "LNRS3T5R",
  "LNRS5T15R",
  "LNRSOV15R",
  "LNOT3LESR",
  "LNOT3T12R",
  "LNOT1T3R",
  "LNOT3T5R",
  "LNOT5T15R",
  "LNOTOV15R",
  "LNRENR4R",
  "LNRENR1R",
  "LNRENR2R",
  "LNRENR3R",
  "LNCI4R",
  "LNCI1R",
  "LNCI2R",
  "LNCI3R",
  "LNREAG4R",
  "LNREAG1R",
  "LNREAG2R",
  "LNREAG3R",
  "LNAG4R",
  "LNAG1R",
  "LNAG2R",
  "LNAG3R",
  "LNRENR4NR",
  "LNRENR1NR",
  "LNRENR2NR",
  "LNRENR3NR",
  "LNCI4NR",
  "LNCI1NR",
  "LNCI2NR",
  "LNCI3NR",
  "LNREAG4NR",
  "LNREAG1NR",
  "LNREAG2NR",
  "LNREAG3NR",
  "LNAG4NR",
  "LNAG1NR",
  "LNAG2NR",
  "LNAG3NR",
  "PPPLNNUMR",
  "PPPLNBALR",
  "PPPLNPLGR",
  "PPPLF1LSR",
  "PPPLFOV1R",
  "AVPPPPLGR",
  "MMLFBALR",
  "AVMMLFR",
  "BKPREMR",
  "ORER",
  "OREINVR",
  "OREOTHR",
  "ORERESR",
  "OREMULTR",
  "ORENRESR",
  "ORECONSR",
  "OREAGR",
  "OREOTHFR",
  "INTANR",
  "INTANGWR",
  "INTANMSRR",
  "INTANOTHR",
  "LIABEQR",
  "LIABR",
  "DEPR",
  "DEPDOMR",
  "TRNR",
  "TRNIPCOCR",
  "TRNUSGOVR",
  "TRNMUNIR",
  "TRNCBOR",
  "TRNFCFGR",
  "NTRR",
  "NTRIPCR",
  "NTRUSGOVR",
  "NTRMUNIR",
  "NTRCOMOTR",
  "NTRFCFGR",
  "DEPFORR",
  "DEPIPCCFR",
  "DEPUSBKFR",
  "DEPFBKFR",
  "DEPFGOVFR",
  "DEPUSMFR",
  "DEPNIFORR",
  "DEPIFORR",
  "DDTR",
  "NTRSMMDAR",
  "NTRSOTHR",
  "TSR",
  "DEPNIDOMR",
  "DEPIDOMR",
  "COREDEPR",
  "DEPINSR",
  "DEPUNAR",
  "IRAKEOGHR",
  "BROR",
  "BROINSR",
  "DEPLSNBR",
  "DEPCSBQR",
  "DEPSMAMTR",
  "DEPLGAMTR",
  "DEPSMRAR",
  "DEPLGRAR",
  "TRNNIAR",
  "NTRCDSMR",
  "NTRTMMEDR",
  "NTRTMLGJR",
  "CD3LESSR",
  "CD3T12SR",
  "CD1T3SR",
  "CDOV3SR",
  "CD3LESR",
  "CD3T12R",
  "CD1T3R",
  "CDOV3R",
  "FREPPR",
  "TRADELR",
  "OTHBRFR",
  "OTBFH1LR",
  "OTBFH1T3R",
  "OTBFH3T5R",
  "OTBFHOV5R",
  "OTBFHSTAR",
  "OTBOT1LR",
  "OTBOT1T3R",
  "OTBOT3T5R",
  "OTBOTOV5R",
  "OTHBOT1LR",
  "ALLOTHLR",
  "EQTOTR",
  "EQR",
  "EQPPR",
  "EQCSR",
  "EQSURR",
  "EQUPTOTR",
  "EQCPREVR",
  "EQCRESTR",
  "EQCTRSTXR",
  "EQCMRGR",
  "EQCCOMPIR",
  "EQCBHCTRR",
  "ASSTLTR",
  "ERNASTR",
  "OALIFINSR",
  "OALIFGENR",
  "OALIFHYBR",
  "OALIFSEPR",
  "AVASSETJR",
  "RWAJTR",
  "RBCT2R",
  "RBCT1JR",
  "OTHBFHLBR",
  "VOLIABR",
  "P3ASSETR",
  "P3RER",
  "P3RENUSR",
  "P3RECNFMR",
  "P3RECNOTR",
  "P3REAGR",
  "P3RERESR",
  "P3RERSFMR",
  "P3RERSF2R",
  "P3RELOCR",
  "P3REMULTR",
  "P3RENROWR",
  "P3RENROTR",
  "P3REFORR",
  "P3DEPR",
  "P3DEPNUSR",
  "P3AGR",
  "P3CIR",
  "P3CINUSR",
  "P3CONR",
  "P3CRCDR",
  "P3AUTOR",
  "P3CONOTHR",
  "P3FGR",
  "P3RECONSR",
  "P3OTHLNR",
  "P3LSR",
  "P3SCDEBTR",
  "P3AGSMR",
  "P3LNSALER",
  "P3GTYPARR",
  "P3GTYR",
  "P3GTYGNMR",
  "P9ASSETR",
  "P9RER",
  "P9RENUSR",
  "P9RECONSR",
  "P9RECNFMR",
  "P9RECNOTR",
  "P9REAGR",
  "P9RERESR",
  "P9RERSFMR",
  "P9RERSF2R",
  "P9RELOCR",
  "P9REMULTR",
  "P9RENRESR",
  "P9RENROWR",
  "P9RENROTR",
  "P9REFORR",
  "P9DEPR",
  "P9DEPNUSR",
  "P9AGR",
  "P9CIR",
  "P9CINUSR",
  "P9CONR",
  "P9CRCDR",
  "P9AUTOR",
  "P9CONOTHR",
  "P9FGR",
  "P9OTHLNR",
  "P9LSR",
  "P9SCDEBTR",
  "P9AGSMR",
  "P9LNSALER",
  "P9GTYPARR",
  "P9GTYR",
  "P9GTYGNMR",
  "NASSETR",
  "NARER",
  "NARENUSR",
  "NARECONSR",
  "NARECNFMR",
  "NARECNOTR",
  "NAREAGR",
  "NARERESR",
  "NARERSFMR",
  "NARERSF2R",
  "NARELOCR",
  "NAREMULTR",
  "NARENRESR",
  "NARENROWR",
  "NARENROTR",
  "NAREFORR",
  "NADEPR",
  "NADEPNUSR",
  "NAAGR",
  "NACIR",
  "NACINUSR",
  "NACONR",
  "NACRCDR",
  "NAAUTOR",
  "NACONOTHR",
  "NAFGR",
  "NAOTHLNR",
  "NALSR",
  "NASCDEBTR",
  "NAAGSMR",
  "NALNSALER",
  "NAGTYPARR",
  "NAGTYR",
  "NAGTYGNMR",
  "LSALNLSR",
  "LRECONSR",
  "LREAGR",
  "LRERESR",
  "LREMULTR",
  "LRENRESR",
  "LCIR",
  "LCONR",
  "LAGR",
  "LOTHR",
  "P3LTOTR",
  "P3LRECONR",
  "P3LREAGR",
  "P3LRERESR",
  "P3LREMULR",
  "P3LRENRSR",
  "P3LCIR",
  "P3LCONR",
  "P3LAGR",
  "P3LOTHR",
  "P3LGTYR",
  "P9LTOTR",
  "P9LRECONR",
  "P9LREAGR",
  "P9LRERESR",
  "P9LREMULR",
  "P9LRENRSR",
  "P9LCIR",
  "P9LCONR",
  "P9LAGR",
  "P9LOTHR",
  "P9LGTYR",
  "NALTOTR",
  "NALRECONR",
  "NALREAGR",
  "NALRERESR",
  "NALREMULR",
  "NALRENRSR",
  "NALCIR",
  "NALCONR",
  "NALAGR",
  "NALOTHR",
  "NALGTYR",
  "LSAORER",
  "LOREGTYR",
  "LSASCDBTR",
  "LSAOAR",
  "UCR",
  "UCLOCR",
  "UCCRCDR",
  "UCCOMRER",
  "UCCOMRESR",
  "UCCOMREUR",
  "UCSCR",
  "UCOVER1R",
  "SCLENTR",
  "OTHOFFBSR",
  "PARTCONVR",
  "LOCFPSBR",
  "LOCFPSBKR",
  "LOCFSBR",
  "LOCFSBKR",
  "LOCPSBR",
  "LOCPSBKR",
  "LOCCOMR",
  "SZLNRESR",
  "SZISLRESR",
  "SZ30RESR",
  "SZ90RESR",
  "SZDRRESR",
  "SZCRRESR",
  "SZLNHELR",
  "SZISLHELR",
  "SZ30HELR",
  "SZ90HELR",
  "SZDRHELR",
  "SZCRHELR",
  "SZLNCRCDR",
  "SZISLCCDR",
  "SZ30CRCDR",
  "SZ90CRCDR",
  "SZDRCRCDR",
  "SZCRCRCDR",
  "SZLAUTOR",
  "SZISLAUTR",
  "SZ30AUTOR",
  "SZ90AUTOR",
  "SZDRAUTOR",
  "SZCRAUTOR",
  "SZLNCONR",
  "SZISLCONR",
  "SZ30CONR",
  "SZ90CONR",
  "SZDRCONR",
  "SZCRCONR",
  "SZLNCIR",
  "SZISLCIR",
  "SZ30CIR",
  "SZ90CIR",
  "SZDRCIR",
  "SZCRCIR",
  "SZLNOTHR",
  "SZISLOTHR",
  "SZ30OTHR",
  "SZ90OTHR",
  "SZDROTHR",
  "SZCROTHR",
  "ENCERESR",
  "ENCEAUTOR",
  "ENCECONR",
  "ENCECIR",
  "ENCEOTHR",
  "ASDRRESR",
  "ASCERESR",
  "ASDROTHR",
  "ASCEOTHR",
  "MSRECER",
  "MSRNRECER",
  "LNSERVR",
  "MSRESFCLR",
  "ABCXBKR",
  "ABCXOTHR",
  "ABCUBKR",
  "ABCUOTHR",
  "SZCRCDFER",
];

const PERFORMANCE_CONDITIONS_YTD = [
  "NTINCL",
  "NTINCHPP",
  "INTINCY",
  "INTEXPY",
  "NIMY",
  "NONIIAY",
  "NONIXAY",
  "ELNATRY",
  "NOIJY",
  "ROA",
  "ROAPTX",
  "ROE",
  "ROEINJR",
  "NTLNLSR",
  "NTRER",
  "NTRECOSR",
  "NTRENRSR",
  "NTREMULR",
  "NTRERESR",
  "NTRELOCR",
  "NTREOTHR",
  "IDNTCIR",
  "IDNTCONR",
  "IDNTCRDR",
  "IDNTCOOR",
  "NTAUTOPR",
  "NTCONOTR",
  "NTALLOTHR",
  "NTCOMRER",
  "ELNANTR",
  "IDERNCVR",
  "EEFFR",
  "ASTEMPM",
  "EQCDIVNTINC",
  "LNATRESR",
  "LNRESNCR",
  "NPERFV",
  "NCLNLSR",
  "NCRER",
  "NCRECONR",
  "NCRENRER",
  "NCREMULR",
  "NCRERESR",
  "NCRELOCR",
  "NCREREOR",
  "IDNCCIR",
  "IDNCCONR",
  "IDNCCRDR",
  "IDNCCOOR",
  "IDNCATOR",
  "IDNCCOTR",
  "IDNCOTHR",
  "NCCOMRER",
  "IDNCGTPR",
  "LNLSNTV",
  "LNLSDEPR",
  "IDLNCORR",
  "DEPDASTR",
  "EQV",
  "RBC1AAJ",
  "CBLRIND",
  "IDT1CER",
  "IDT1RWAJR",
  "RBCRWAJ",
];

const PERFORMANCE_CONDITIONS_QTLY = [
  "NTINCLQ",
  "NTINQHPP",
  "INTINCYQ",
  "INTEXPYQ",
  "NIMYQ",
  "NONIIAYQ",
  "NONIXAYQ",
  "ELNATRYQ",
  "NOIJYQ",
  "ROAQ",
  "ROAPTXQ",
  "ROEQ",
  "NTLNLSQR",
  "NTREQR",
  "NTRECOQR",
  "NTRENRQR",
  "NTREMUQR",
  "NTRERESRQ",
  "NTRELOCRQ",
  "NTREOTHRQR",
  "IDNTCIQR",
  "IDNTCRDQR",
  "IDNTCOOQR",
  "NTAUTOQR",
  "NTCONTQR",
  "NTALLOTHQR",
  "NTCOMREQR",
  "ELNATQR",
  "IDERNCVQR",
  "EEFFQR",
];
export const VARIABLES_TO_SKIP = [
  "OFFFOR",
  "DEPFOR",
  "DEPIFOR",
  "DEPNIFOR",
  "EDEPFOR",
  "ELNATR",
  "EQSUR",
  "FORCHRTR",
  "FORMCFR",
  "ILNFOR",
  "LNLSGR",
  "LNOTHER",
  "LNREFOR",
  "NTR",
  "OTHBOR",
  "STCHRTR",
  "TTLOTBOR",
  "ASSETFOR",
  "CHBALFOR",
  "CROTHER",
  "CRREFOR",
  "DROTHER",
  "DRREFOR",
  "FORMTFR",
  "IBEFXTR",
  "INTANMSR",
  "LIABFOR",
  "LNAGFOR",
  "LNCONFOR",
  "LNFGFOR",
  "LNLSFOR",
  "LNSBR",
  "LSFOR",
  "NAGTYPAR",
  "NAREFOR",
  "NCGTYPAR",
  "NTOTHER",
  "NTREFOR",
  "OBSDIR",
  "P3GTYPAR",
  "P3REFOR",
  "P9GTYPAR",
  "P9REFOR",
  "RBCT1CER",
  "RSOTHER",
  "TIOR",
  "TIR",
  "TRACT",
  "TREXER",
  "TRFOR",
  "TRPOWER",
  "UCOTHER",
  "UNINCFOR",
  "CALLFORM",
  "CBLRIND",
];

export const ALL_RATIO_FIELDS = [
  ...INCOME_EXPENSE_YTD,
  ...INCOME_EXPENSE_QTLY,
  ...PERFORMANCE_CONDITIONS_YTD,
  ...PERFORMANCE_CONDITIONS_QTLY,
  ...ASSET_LIABILITIES_RATIO,
  ...OTHER_RATIO_CONDITIONS,
];

export const getFilters = (
  index: number,
  group: KeyValue[],
  repDate: string,
  top100Certs: string[],
) => {
  let isStandardPG = group[index]?.is_standard;

  const data = isStandardPG
    ? group[index].filterCriteria
    : group[index]?.appState || group[index].filterCriteria;

  if (data?.filters) {
    const updatedState = {
      ...data.filters,
      reportPeriod: [repDate],
    };
    const dataFilters = (generateFinancialsApiUrl(
      updatedState,
      undefined,
      "",
      false,
      true,
    ) as KeyValue).filters;
    const top100Query =
      top100Certs.length > 0 ? ` AND CERT:(${top100Certs.join(" OR ")})` : "";
    return dataFilters ? dataFilters.replace(" AND top100", top100Query) : "";
  } else {
    const joinedData = data
      .map((val: KeyValue) => {
        return `${val.variable}:${val.value}`;
      })
      .join(" AND ");
    return joinedData
      ? `RISDATE:${repDate} AND INSALL:1 AND ${joinedData}`
      : "";
  }
};

export const getSearch = (index: number, group: KeyValue[]) => {
  const data = group[index]?.appState ? group[index].appState : undefined;
  if (data?.search) {
    return (generateFinancialsApiUrl(
      data,
      undefined,
      "",
      false,
      true,
    ) as KeyValue).search;
  }
  return "";
};
